import { useState, useEffect, useRef } from 'react';

interface UseInViewOptions {
  threshold?: number;
  triggerOnce?: boolean;
  rootMargin?: string;
}

/**
 * Hook personalizzato per rilevare quando un elemento è visibile nel viewport
 * @param options - Opzioni per l'Intersection Observer
 * @returns [ref, inView] - Riferimento da collegare all'elemento e booleano che indica se è visibile
 */
const useInView = (options: UseInViewOptions = {}) => {
  const { 
    threshold = 0.1, 
    triggerOnce = false,
    rootMargin = '0px'
  } = options;
  
  const [inView, setInView] = useState(false);
  const ref = useRef<HTMLElement | null>(null);
  const observerRef = useRef<IntersectionObserver | null>(null);
  
  useEffect(() => {
    const element = ref.current;
    if (!element) return;
    
    // Pulizia del vecchio observer se esiste
    if (observerRef.current) {
      observerRef.current.disconnect();
    }
    
    // Creazione del nuovo observer
    observerRef.current = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        setInView(entry.isIntersecting);
        
        // Se triggerOnce è true e l'elemento è entrato nel viewport,
        // scolleghiamo l'observer per non triggerare più
        if (triggerOnce && entry.isIntersecting && observerRef.current) {
          observerRef.current.unobserve(element);
        }
      },
      { threshold, rootMargin }
    );
    
    // Osserva l'elemento attuale
    observerRef.current.observe(element);
    
    // Pulizia quando il componente viene smontato
    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, [threshold, triggerOnce, rootMargin]);
  
  return [ref, inView] as const;
};

export default useInView; 